import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { useMyRestaurants } from '../hooks/useMyRestaurants';

interface Account {
  visits_count: number;
  created_at: string;
  updated_at: string;
  full_name: string | null;
}

const DAY = 24 * 60 * 60 * 1000;
const VIP_MIN_VISITS = 10;

export function CrmPage() {
  const { t } = useTranslation();
  const { restaurants, loading } = useMyRestaurants();
  const restaurant = restaurants[0];
  const [accounts, setAccounts] = useState<Account[]>([]);

  const reload = useCallback(async () => {
    if (!restaurant) return;
    const { data } = await supabase
      .from('loyalty_accounts')
      .select('visits_count, created_at, updated_at, profiles(full_name)')
      .eq('restaurant_id', restaurant.id);
    if (data) {
      setAccounts(
        (data as unknown as Array<Record<string, unknown>>).map((a) => ({
          visits_count: a.visits_count as number,
          created_at: a.created_at as string,
          updated_at: a.updated_at as string,
          full_name: ((a.profiles as { full_name?: string } | null)?.full_name) ?? null
        }))
      );
    }
  }, [restaurant]);

  useEffect(() => { void reload(); }, [reload]);

  const segments = useMemo(() => {
    const now = Date.now();
    const vip: Account[] = [];
    const novato: Account[] = [];
    const risco: Account[] = [];
    for (const a of accounts) {
      const sinceLast = now - new Date(a.updated_at).getTime();
      const sinceFirst = now - new Date(a.created_at).getTime();
      if (sinceLast > 30 * DAY) risco.push(a);
      else if (sinceFirst <= 7 * DAY) novato.push(a);
      else if (a.visits_count >= VIP_MIN_VISITS) vip.push(a);
    }
    return { vip, novato, risco };
  }, [accounts]);

  if (loading || !restaurant) return <div className="center-note">{t('common.loading')}</div>;

  const column = (titleKey: string, descKey: string, list: Account[]) => (
    <div className="kds-col">
      <h2 className="kds-col__title">
        {t(`crm.${titleKey}`)} <span className="tag">{list.length}</span>
      </h2>
      <p className="muted-note">{t(`crm.${descKey}`)}</p>
      {list.length === 0 ? (
        <p className="muted-note">{t('crm.none')}</p>
      ) : (
        list.map((a, i) => (
          <div key={i} className="kds-card">
            <strong>{a.full_name || '—'}</strong>
            <div className="muted-note">{a.visits_count} {t('crm.visits')}</div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="page page--wide">
      <h1 className="page__title">{t('crm.title')}</h1>
      <div className="kds-board">
        {column('vip', 'vip_desc', segments.vip)}
        {column('novato', 'novato_desc', segments.novato)}
        {column('risco', 'risco_desc', segments.risco)}
      </div>
    </div>
  );
}
